import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Settings as SettingsIcon, LogOut, Sparkles,
  Menu, ChevronDown, Building2, User as UserIcon, PlayCircle,
  BookOpen as PlaybookIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Wordmark } from "@/components/Wordmark";
import { MODULES, moduleColorClass } from "@/lib/modules";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ImpersonationBanner, isImpersonating } from "@/components/ImpersonationBanner";
import { NotificationBell } from "@/components/NotificationBell";
import { OnboardingWizard, openWelcomeTour } from "@/components/OnboardingWizard";
import { SamHelper } from "@/components/SamHelper";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { api } from "@/lib/api";
import { toast } from "sonner";

function navItemClass({ isActive }) {
  return [
    "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
    isActive
      ? "bg-gradient-to-r from-indigo-500/15 via-violet-500/10 to-fuchsia-500/15 text-white border border-white/10"
      : "text-white/55 hover:text-white hover:bg-white/5",
  ].join(" ");
}

export function AppShell() {
  const { user, org, orgs, logout, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user || !org) {
    return (
      <div className="flex h-screen items-center justify-center text-white/50" data-testid="appshell-loading">
        Loading workspace…
      </div>
    );
  }

  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  const visibleModules = MODULES.filter((m) => !m.superAdminOnly || user.is_super_admin);

  const Sidebar = (
    <aside className="flex h-full w-72 flex-col border-r border-white/5 bg-[#050505]/80 backdrop-blur-xl">
      <div className="flex h-16 items-center px-5 border-b border-white/5">
        <Link to="/app" className="inline-flex items-center" aria-label="Symplee — home">
          <Wordmark variant="dark" size="sm" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-5">
        <div className="space-y-1">
          <NavLink
            to="/app" end className={navItemClass} data-testid="sidebar-home"
            onClick={() => setMobileOpen(false)}
          >
            <LayoutDashboard className="h-4 w-4" />
            Home
          </NavLink>
        </div>

        <div className="mt-6">
          <div className="px-3 pb-2 text-eyebrow text-white/30">Modules</div>
          <div className="space-y-1">
            {visibleModules.map((m) => (
              <NavLink
                key={m.slug}
                to={`/app/${m.slug}`}
                className={navItemClass}
                data-testid={`sidebar-module-${m.slug}`}
                onClick={() => setMobileOpen(false)}
              >
                {/* Iter 24 (audit Finding 9) — per-module accent color so the
                    sidebar reads as distinct surfaces, not one undifferentiated
                    list of fuchsia links. */}
                <m.icon className={`h-4 w-4 ${moduleColorClass(m.color)}`} />
                <span className="flex-1">{m.name}</span>
                {m.superAdminOnly ? (
                  <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300">
                    Super
                  </span>
                ) : (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                      m.live
                        ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : "bg-white/5 text-white/40"
                    }`}
                  >
                    {m.live ? "Live" : "Soon"}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {/* Iter 27 — Sales Playbook nav is internal-only. Customer orgs
              never see this link (the playbook route also 404s for them). */}
          {org?.is_symplee_internal && (
            <>
              <div className="px-3 pb-2 text-eyebrow text-white/30">Sales</div>
              <NavLink
                to="/app/playbook" className={navItemClass} data-testid="sidebar-playbook"
                onClick={() => setMobileOpen(false)}
              >
                <PlaybookIcon className="h-4 w-4" />
                <span className="flex-1">Sales Playbook</span>
                <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] text-indigo-300">Internal</span>
              </NavLink>
            </>
          )}
        </div>

        <div className="mt-6">
          <div className="px-3 pb-2 text-eyebrow text-white/30">Account</div>
          <NavLink
            to="/app/settings" className={navItemClass} data-testid="sidebar-settings"
            onClick={() => setMobileOpen(false)}
          >
            <SettingsIcon className="h-4 w-4" />
            Settings
          </NavLink>
        </div>
      </nav>

      <div className="border-t border-white/5 p-4">
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/70 transition hover:bg-white/10 hover:text-white"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
    </aside>
  );

  const impersonating = isImpersonating();

  return (
    <div className="relative min-h-screen bg-[#050505] text-white">
      <ImpersonationBanner />
      <div className="aurora opacity-30" />
      <div className="grain" />

      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-40 hidden md:flex">{Sidebar}</div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 shadow-2xl">
            {Sidebar}
          </div>
        </div>
      )}

      <div className="relative z-10 md:pl-72">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/5 bg-[#050505]/80 px-4 backdrop-blur-xl md:px-8">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>

            <OrgSwitcherInline current={org} orgs={orgs} onSwitch={switchOrg} />
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1 pr-3 transition hover:bg-white/10"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-7 w-7">
                    {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-xs text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm text-white/80 sm:inline">{user.name}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-white/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuLabel>
                  <div className="text-sm">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                  {user.is_super_admin && (
                    <div className="mt-1 inline-block rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-300">
                      Super admin
                    </div>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="usermenu-settings">
                  <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="usermenu-profile">
                  <UserIcon className="mr-2 h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await api.post("/users/me/reset-welcome-tour");
                      openWelcomeTour();
                    } catch (e) {
                      // Open anyway — server reset is best-effort
                      openWelcomeTour();
                      toast.error("Could not reset tour state — showing it anyway.");
                    }
                  }}
                  data-testid="usermenu-replay-tour"
                >
                  <PlayCircle className="mr-2 h-4 w-4 text-fuchsia-300" /> Take the tour again
                </DropdownMenuItem>
                {user.is_super_admin && (
                  <DropdownMenuItem onClick={() => navigate("/app/admin")} data-testid="usermenu-admin">
                    <Sparkles className="mr-2 h-4 w-4 text-amber-300" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-400 focus:text-red-400"
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="usermenu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="relative z-10 px-4 py-8 sm:px-6 md:px-10">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Welcome tour — auto on first visit; re-openable via user menu */}
      {!impersonating && <OnboardingWizard />}

      {/* Sam — in-product AI helper. Visible on every authenticated /app/* page,
          hidden during impersonation so we don't show Sam to admins acting-as. */}
      {!impersonating && <SamHelper />}
    </div>
  );
}

function OrgSwitcherInline({ current, orgs, onSwitch }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 transition hover:bg-white/10"
          data-testid="org-switcher-trigger"
        >
          <Building2 className="h-4 w-4 text-white/60" />
          <span className="max-w-[200px] truncate text-sm text-white">{current.name}</span>
          <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-white/50">
            {current.plan === "pro" ? "Pro" : "Trial"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-white/50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-popover">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
        {orgs.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onClick={() => onSwitch(o.id)}
            data-testid={`org-switcher-item-${o.id}`}
          >
            <Building2 className="mr-2 h-4 w-4" />
            <div className="flex-1">
              <div className="text-sm">{o.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{o.role} · {o.plan === "pro" ? "Pro" : "Trial"}</div>
            </div>
            {o.id === current.id && <span className="text-[10px] text-white/60">Current</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
